<template>
  <div class="c-product-grid">
    <div class="l-container">
      <h2
        v-if="!filtered"
        class="t-heading-3 u-margin-bottom-x-small"
      >
        {{ name }}
      </h2>
      <p v-if="products.length == 0 && loading">
        Loading ...
      </p>
      <h5
        v-else-if="products.length && !filtered"
        class="t-heading-7 u-margin-bottom-medium"
      >
        {{ products.length }} Results
      </h5>
      <p v-else-if="!filtered">
        No products to display.
      </p>
      <product-bundle-card
        v-if="showBundleCard"
        :product="bundleProduct"
        :bundle="bundle"
        :active-options="activeOptions"
        @update:selection="addToBundle"
        @modal:open="$emit('modal:open')"
        @engraving="$emit('engraving', $event)"
      />
      <div
        v-else
        class="l-grid"
        :class="{
          'l-grid--2-col': columns === 2,
          'l-grid--3-col': columns === 3,
          'l-grid--4-col-min-2': columns === 4,
        }"
      >
        <div
          v-for="(product, index) in visibleProducts"
          :key="`${product.id}--${index}`"
          class="l-grid__item l-grid__item--align-start"
        >
          <product-card
            :key="`j-${product.id}`"
            :product="product"
            :bundle="bundle"
            :title="product && product.title"
            :enable-stars="enableStars"
            :compact="compact"
            :bundle-slot-count="bundleSlotCount"
            :irregular-prices="irregularPrices"
            :disable-button="disableProductShopButton"
            @click="$emit('click', $event)"
            @select="openBundleCard"
          />
        </div>
        <div
          v-if="showMoreCard"
          class="l-grid__item l-grid__item--align-start"
        >
          <div
            class="c-product-grid__show-more"
            :aria-expanded="showCardsCount > productList.length ? 'true' : 'false'"
            role="button"
            @click.prevent="moreRings"
          >
            <content-card
              background-color="#FAFBFB"
              :image="moreFeaturedMedia"
              link-text="View More"
              :subtitle="`${products.length - showCardsCount + 1} MORE COLORS`"
              :description="`<b>${products.length - showCardsCount + 1} MORE COLORS</b><br/>
                <a role='link' href='' style='color:#022840'>View More</a>`"
              description-margin-bottom="small"
              :compact="compact"
              button-type="link"
              cta-link="#link"
              cta-title="View More"
              class="c-product-grid__more-card"
            />
          </div>
        </div>
        <div
          v-if="showModCard"
          class="l-grid__item l-grid__item--align-start"
        >
          <div
            class="c-product-grid__show-more"
          >
            <background-content-card
              :compact="compact"
              v-bind="buildYourOwn"
              class="c-product-grid__more-card"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BackgroundContentCard from '../card/BackgroundContentCard'
import clone from 'lodash/cloneDeep'
import ContentCard from '../card/ContentCard'
import get from 'lodash/get'
import ProductBundleCard from "../card/ProductBundleCard.vue"
import ProductCard from '../card/ProductCard'
import { sortProducts } from '@/utils/shopifyHelper'

export default {
  name: "ProductGrid",
  components:{
    ProductCard,
    ContentCard,
    BackgroundContentCard,
    ProductBundleCard
  },
  props: {
    products: {
      type: Array,
      default: () => []
    },
    name: {
      type: String,
      default: ''
    },
    filtered: {
      type: Boolean,
      default: false,
    },
    truncate: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Number,
      default: 4,
    },
    enableStars: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    collectionHandle: {
      type: String,
      default: ''
    },
    maxProducts: {
      type: Number,
      default: 16
    },
    bundle: {
      type: Object,
      default: () => {}
    },
    bundleSlotCount: {
      type: Number,
      default: 0
    },
    irregularPrices: {
      type: Boolean,
      default: false,
    },
    activeOptions: {
      type: Array,
      default: () => []
    },
    bundleProduct: {
      type: Object,
      default: null
    },
    disableProductShopButton: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showCardsCount: this.maxProducts,
      buildYourOwn: {
        title: 'Build Your Own',
        titleTypography: 't-heading-4',
        cta: {
          url: '/pages/mod-builder',
          text: 'build now'
        }
      }
    }
  },
  computed: {
    showBundleCard() {
      const bundleProduct = this.bundleProduct
      //const activeOptions = this.activeOptions
      let result = false
      if (bundleProduct) {
        result = true
        /*
        for (let option of activeOptions) {
          if (
            option &&
            option.product &&
            (option.product.id === bundleProduct.id)
          ) {
            result = false
            break
          }
        }
        */
      }
      return result
    },
    showModCard() {
      const collectionHandle = this.collectionHandle
      const ENABLE_MOD_CARD = process.env.ENABLE_MOD_CARD
      return (
        (collectionHandle === 'mod') &&
        ENABLE_MOD_CARD
      )
    },
    productList() {
      let products = this.products
      let results = []
      for (let product of products) {
        let color = get(product, 'metafieldsMap.color.value.name')
        let verificationColor = null
        // verify metafield data. should we throw if it doesn't match?
        for (let { key, value } of product.metafields) {
          if (key === 'color') {
            const parsedValue = JSON.parse(value)
            verificationColor = parsedValue.name
            break
          }
        }
        if (color !== verificationColor) {
          console.warn(
            product.handle,
            'invalid metafieldsMap.color.value detected!',
            {
              metafields: product.metafields,
              mapValue: get(product, 'metafieldsMap.color.value')
            }
          )
          if (process.env.FEAT_THROW_INVALID_COLOR) {
            throw new Error(`InvalidColorMetafield<${product.handle}>`)
          } else {
            color = verificationColor
          }
        }
        results.push({
          ...clone(product),
          title: color || product.title,
        })
      }
      if (this.sorted) {
        return results
      }
      return sortProducts(results)
    },
    visibleProducts() {
      const limit = this.showCardsCount
      const productList = this.productList
      const truncate = this.truncate
      const showCardsCount = truncate
        ? limit
        : productList.length
      let result = []
      // would be nice to refactor this, the logic is hard to follow
      // you've got that right jeez
      for (let [index, product] of Object.entries(productList)) {
        if (
          this.showGrid(index, showCardsCount, productList) &&
          this.showProductCard(index, showCardsCount, productList)
        ) {
          result.push(product)
        }
      }
      return result
    },
    showMoreCard() {
      const productList = this.productList
      const visibleProducts = this.visibleProducts
      const index = visibleProducts.length
      const showCardsCount = this.showCardsCount
      return (
        index == showCardsCount - 1 &&
        productList.length > showCardsCount &&
        index < showCardsCount
      )
    },
    moreFeaturedMedia() {
      const productList = this.productList
      const visibleProducts = this.visibleProducts
      const product = productList[visibleProducts.length]
      return product && product.featuredMedia
    },
  },
  watch: {
    filtered(filtered) {
      if (filtered){
        this.showCardsCount = this.productList.length
      }
    }
  },
  methods: {
    moreRings() {
      this.showCardsCount = this.productList.length
    },
    showGrid(index, showCardsCount, productList) {
      return (
        (index < showCardsCount-1) ||
        (
          index == showCardsCount - 1 &&
          productList.length >= showCardsCount &&
          index < showCardsCount
        )
      )
    },
    showProductCard(index, showCardsCount, productList) {
      return (
        index < showCardsCount - 1 ||
        (index == showCardsCount - 1 && productList.length == showCardsCount)
      )
    },
    openBundleCard(value, bundleProduct){
      this.$emit('update:bundleProduct', bundleProduct)
    },
    addToBundle(event) {
      this.$emit("update:selection", event)
      this.$emit('update:bundleProduct', null)
    }
  },
}
</script>

<style lang="scss">
.c-product-grid {
  padding: $section-padding;
  &__show-more {
    cursor: pointer;
  }
  // jgunther this had to break some rules may want to refactor and call a
  // version of product card instead in the future
  &__more-card {
    height: rem(375px);
    .c-content-card__image {
      width: rem(193px);
      max-width: 59%;
      max-height: 100%;
    }
    .c-content-card__content-container {
      height: 100%;
    }
    .c-content-card__content {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      align-items: center;
      justify-content: space-evenly;
    }
  }
}
</style>
